"use client";

import { useForm, FormProvider, useFieldArray } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { LoadingSpinner } from "@/components/LoadingSpinner";
import { CustomerService } from "@/services/CustomerService";
import { PlusCircle, Search, Trash, User, PhoneIcon, Mail } from "lucide-react"; // Added PhoneIcon
import type { CustomersInput, CustomersOutput } from "@/interfaces"; // Assuming CustomersOutput might have flat phone fields
import { fetchAddressByZipCode } from "@/util/fetchCep";
import MultiTagSelector from "@/components/MultiTagSelector";
import { CpfCnpjInput } from "@/components/CpfCnpjInput";
import { Separator } from "@/components/ui/separator";
import { DatePickerComponent } from "@/components/DatePickerComponent";
import { PhoneInputFields } from "@/components/PhoneNumberInput"; // Import the shared phone input
import { customerFormSchema, CustomerFormSchemaType } from "./schema/schema";

const formatZipCode = (value: string) => {
  const digits = value.replace(/\D/g, "").slice(0, 8);
  return digits.replace(/(\d{5})(\d{1,3})$/, "$1-$2");
};

export default function CustomerForm() {
  const navigate = useNavigate();
  const { id } = useParams<{ id?: string }>();
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isCepLoading, setIsCepLoading] = useState<boolean>(false);
  const service = new CustomerService();

  const form = useForm<CustomerFormSchemaType, undefined, CustomerFormSchemaType>({
    resolver: zodResolver(customerFormSchema),
    defaultValues: {
      name: "",
      document: "",
      dateOfBirth: undefined,
      tags: [],
      phone: {
        internationalCode: "55",
        ddd: "",
        phoneNumber: "",
      },
      addresses: [],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: "addresses",
  });

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        setIsEditing(!!id);
        if (id) {
          // Cast to include potential flat phone fields for mapping
          const fetchedCustomer = (await service.findById(
            id
          )) as CustomersOutput & {
            internationalCode?: string;
            ddd?: string;
            phoneNumber?: string;
          };

          form.reset({
            name: fetchedCustomer.name || "",
            document: fetchedCustomer.document || "",
            email: fetchedCustomer.email || "",
            dateOfBirth: fetchedCustomer.dateOfBirth
              ? new Date(fetchedCustomer.dateOfBirth)
              : undefined,
            tags: (fetchedCustomer.tags as string[]) || [], // Assuming tags can be string[]
            phone: {
              internationalCode: fetchedCustomer.internationalCode || "55",
              ddd: fetchedCustomer.ddd || "",
              phoneNumber: fetchedCustomer.phoneNumber || "",
            },
            addresses: fetchedCustomer.addresses || [],
          });
        }
      } catch (error) {
        console.error("Erro ao carregar dados:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchInitialData();
    return () => {
      setIsLoading(true);
    };
  }, [id, form]); // Added service to dependency array

  async function onSubmit(values: CustomerFormSchemaType) {
    try {
      setIsLoading(true);
      const { phone, ...restOfValues } = values;

      // Prepare data for API, mapping nested phone to flat structure
      const customerData: CustomersInput = {
        ...restOfValues,
        name: values.name, // Ensure name is explicitly passed
        document: values.document || undefined, // Ensure empty string becomes undefined
        dateOfBirth: values.dateOfBirth || undefined,
        tags: values.tags || undefined,
        addresses: values.addresses || undefined,
      };

      if (phone && phone.internationalCode && phone.ddd && phone.phoneNumber) {
        customerData.internationalCode = phone.internationalCode;
        customerData.ddd = phone.ddd;
        customerData.phoneNumber = phone.phoneNumber;
      } else {
        customerData.internationalCode = undefined;
        customerData.ddd = undefined;
        customerData.phoneNumber = undefined;
      }

      // Remove empty document value specifically
      if (customerData.document === "") {
        delete customerData.document;
      }

      if (isEditing && id) {
        await service.update(id, customerData);
      } else {
        await service.create(customerData);
      }
      navigate("/clientes");
    } catch (error) {
      console.error("Erro ao salvar cliente:", error);
    } finally {
      setIsLoading(false);
    }
  }

  const handleCepBlur = async (index: number, zipCode?: string) => {
    if (!zipCode || zipCode.replace(/\D/g, "").length < 8) return;

    try {
      setIsCepLoading(true);
      const addressData = await fetchAddressByZipCode(zipCode);
      if (addressData) {
        form.setValue(`addresses.${index}.city`, addressData.localidade || "");
        form.setValue(
          `addresses.${index}.neighborhood`,
          addressData.bairro || ""
        );
        form.setValue(`addresses.${index}.line1`, addressData.logradouro || "");
      }
    } catch (error) {
      console.error("Failed to fetch CEP data:", error);
    } finally {
      setIsCepLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <FormProvider {...form}>
      <div className="p-4 sm:p-6 max-w-2xl mx-auto">
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <Card className="overflow-hidden border-none shadow-md">
            <CardHeader className="bg-gradient-to-r from-[#EE7B11] to-[#f59642] text-white">
              <div className="flex items-center gap-3">
                <User className="h-6 w-6" />
                <CardTitle className="text-xl">
                  {isEditing ? "Editar Cliente" : "Novo Cliente"}
                </CardTitle>
              </div>
            </CardHeader>
            <CardContent className="p-4 sm:p-6 space-y-4">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Nome do cliente</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Nome do cliente"
                        type="text"
                        {...field}
                        className="border-gray-300 focus:border-[#EE7B11] focus:ring-[#EE7B11]"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="document"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>CPF/CNPJ</FormLabel>
                    <FormControl>
                      <CpfCnpjInput
                        value={field.value ?? ""}
                        onChange={field.onChange}
                        className="border-gray-300 focus:border-[#EE7B11] focus:ring-[#EE7B11]"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="flex items-center gap-2 text-sm">
                      <Mail className="h-4 w-4 text-[#EE7B11]" />
                      Email
                    </FormLabel>
                    <FormControl>
                      <Input
                        placeholder="email@example.com"
                        {...field}
                        className="border-gray-300 focus:border-[#EE7B11] focus:ring-[#EE7B11] h-9 text-sm"
                      />
                    </FormControl>
                    <FormMessage className="text-sm" />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="dateOfBirth"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Data de Nascimento</FormLabel>
                    <FormControl>
                      <DatePickerComponent
                        id="dateOfBirth"
                        selected={field.value}
                        placeholder="Selecione a data de nascimento"
                        onSelect={(date) => field.onChange(date)}
                        className="border-gray-300 focus:border-[#EE7B11] focus:ring-[#EE7B11]"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="tags"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Tags</FormLabel>
                    <FormControl>
                      <MultiTagSelector
                        value={field.value || []}
                        onChange={field.onChange}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Phone Input Section */}
              <div className="space-y-1">
                <FormLabel className="text-sm font-medium flex items-center">
                  <PhoneIcon className="h-4 w-4 mr-2 text-[#EE7B11]" />
                  Telefone
                </FormLabel>
                <PhoneInputFields basePath="phone" />
                {form.formState.errors.phone?.internationalCode?.message && (
                  <FormMessage className="text-xs">
                    {form.formState.errors.phone.internationalCode.message}
                  </FormMessage>
                )}
                {form.formState.errors.phone?.ddd?.message && (
                  <FormMessage className="text-xs">
                    {form.formState.errors.phone.ddd.message}
                  </FormMessage>
                )}
                {form.formState.errors.phone?.phoneNumber?.message && (
                  <FormMessage className="text-xs">
                    {form.formState.errors.phone.phoneNumber.message}
                  </FormMessage>
                )}
                {form.formState.errors.phone?.root?.message && (
                  <FormMessage className="text-xs">
                    {form.formState.errors.phone.root.message}
                  </FormMessage>
                )}
              </div>

              {/* Addresses Section */}
              <div className="mt-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold">Endereços</h3>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      append({
                        zipCode: "",
                        city: "",
                        neighborhood: "",
                        line1: "",
                        line2: "",
                        number: "",
                      })
                    }
                    className="border-[#EE7B11] text-[#EE7B11] hover:bg-[#EE7B11]/10"
                  >
                    <PlusCircle className="h-4 w-4 mr-2" /> Adicionar endereço
                  </Button>
                </div>

                {fields.length === 0 && (
                  <div className="text-center py-8 border rounded-lg bg-gray-50">
                    <p className="text-gray-500">Nenhum endereço cadastrado</p>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        append({
                          zipCode: "",
                          city: "",
                          neighborhood: "",
                          line1: "",
                          line2: "",
                          number: "",
                        })
                      }
                      className="mt-2 border-[#EE7B11] text-[#EE7B11] hover:bg-[#EE7B11]/10"
                    >
                      <PlusCircle className="h-4 w-4 mr-2" /> Adicionar endereço
                    </Button>
                  </div>
                )}

                {fields.map((field, index) => (
                  <div
                    key={field.id}
                    className="space-y-4 border p-4 rounded-lg mb-4 bg-gray-50"
                  >
                    <div className="flex justify-between items-center">
                      <h4 className="text-md font-medium">{`Endereço ${
                        index + 1
                      }`}</h4>
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        onClick={() => remove(index)}
                        className="h-8 w-8 border-red-500 text-red-500 hover:bg-red-50"
                      >
                        <Trash className="h-4 w-4" />
                      </Button>
                    </div>

                    <FormField
                      control={form.control}
                      name={`addresses.${index}.zipCode`}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>CEP</FormLabel>
                          <FormControl>
                            <div className="flex gap-2">
                              <Input
                                value={formatZipCode(field.value || "")}
                                onChange={(event) =>
                                  field.onChange(formatZipCode(event.target.value))
                                }
                                placeholder="99999-999"
                                className="border-gray-300 focus:border-[#EE7B11] focus:ring-[#EE7B11]"
                              />
                              <Button
                                type="button"
                                variant="outline"
                                onClick={() =>
                                  handleCepBlur(index, field.value || undefined)
                                }
                                className="border-[#EE7B11] text-[#EE7B11] hover:bg-[#EE7B11]/10"
                                disabled={isCepLoading}
                              >
                                {isCepLoading ? (
                                  <span className="h-4 w-4 border-2 border-[#EE7B11] border-t-transparent rounded-full animate-spin mr-2"></span>
                                ) : (
                                  <Search className="h-4 w-4 mr-2" />
                                )}
                                Buscar CEP
                              </Button>
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <div className="grid grid-cols-2 gap-4">
                      <FormField
                        control={form.control}
                        name={`addresses.${index}.city`}
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Cidade</FormLabel>
                            <FormControl>
                              <Input
                                placeholder="Cidade"
                                type="text"
                                {...field}
                                className="border-gray-300 focus:border-[#EE7B11] focus:ring-[#EE7B11]"
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name={`addresses.${index}.neighborhood`}
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Bairro</FormLabel>
                            <FormControl>
                              <Input
                                placeholder="Bairro"
                                type="text"
                                {...field}
                                className="border-gray-300 focus:border-[#EE7B11] focus:ring-[#EE7B11]"
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <Separator className="my-2" />

                    <div className="grid grid-cols-4 gap-4">
                      <div className="col-span-3">
                        <FormField
                          control={form.control}
                          name={`addresses.${index}.line1`}
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Endereço</FormLabel>
                              <FormControl>
                                <Input
                                  placeholder="Endereço"
                                  type="text"
                                  {...field}
                                  className="border-gray-300 focus:border-[#EE7B11] focus:ring-[#EE7B11]"
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>

                      <FormField
                        control={form.control}
                        name={`addresses.${index}.number`}
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Número</FormLabel>
                            <FormControl>
                              <Input
                                placeholder="123"
                                type="text"
                                {...field}
                                className="border-gray-300 focus:border-[#EE7B11] focus:ring-[#EE7B11]"
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <FormField
                      control={form.control}
                      name={`addresses.${index}.line2`}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Complemento</FormLabel>
                          <FormControl>
                            <Input
                              placeholder="apto 123"
                              type="text"
                              {...field}
                              className="border-gray-300 focus:border-[#EE7B11] focus:ring-[#EE7B11]"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                ))}
              </div>
            </CardContent>
            <CardFooter className="border-t p-4 sm:p-6 flex justify-end gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate("/clientes")}
                className="border-gray-200 text-gray-700 hover:bg-gray-50"
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                className="bg-[#EE7B11] hover:bg-[#d06a0a] text-white"
                disabled={isLoading}
              >
                {isLoading ? (
                  <LoadingSpinner className="h-4 w-4" />
                ) : isEditing ? (
                  "Atualizar"
                ) : (
                  "Salvar"
                )}
              </Button>
            </CardFooter>
          </Card>
        </form>
      </div>
    </FormProvider>
  );
}





